import t from 'react-translate';
import {
  ReactComponent as InsightGrow,
} from 'styles/icons/insight-grow.svg';
import {
  ReactComponent as Like,
} from 'styles/icons/went-well-like.svg';
import { css } from '@emotion/react';
import { gray5, hexToRgbaString, lowTide } from 'styles/global_defaults/colors';
import { dunes, seafoam } from 'athena/styles/colors';
import prodPathReplace from 'shared/prod-path-rewrite';
import NvGradientBorderButton from 'shared/components/button/nv-gradient-border-button';
import NvIcon from 'shared/components/nv-icon';
import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import { useContext } from 'react';
import AiConversationContext from '../context';

export enum Evaluation {
  NEED_WORK = 'needs_work',
  GOOD_JOB = 'good_job',
  EXCELLENT = 'excellent',
}

type RolePlayFeedbackProps = {
  evaluation: Evaluation;
  overallSummary: string;
  whereYouDidWell?: string[];
  whereYouCanGrow?: string[];
};

const RolePlayFeedback = (props: RolePlayFeedbackProps) => {
  const {
    evaluation,
    overallSummary,
    whereYouDidWell,
    whereYouCanGrow,
  } = props;

  const {
    isRolePlayStarting,
    isHardDeadlineExpired,
    handleStartConversation,
  } = useContext(AiConversationContext);

  const getHeaderBackGroundColor = () => {
    switch (evaluation) {
      case Evaluation.NEED_WORK:
        return hexToRgbaString(dunes, 0.3);
      case Evaluation.GOOD_JOB:
        return hexToRgbaString(lowTide, 0.3);
      case Evaluation.EXCELLENT:
        return hexToRgbaString(seafoam, 0.3);
      default:
        return 'transparent';
    }
  };

  const getHeaderText = () => {
    switch (evaluation) {
      case Evaluation.NEED_WORK:
        return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.NEEDS_WORK();
      case Evaluation.GOOD_JOB:
        return t.SHARED.GOOD_JOB();
      case Evaluation.EXCELLENT:
        return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXCELLENT();
      default:
        return '';
    }
  };

  const renderBadge = () => {
    switch (evaluation) {
      case Evaluation.NEED_WORK:
        return (
          <img
            className='image-badge'
            src={prodPathReplace('images/need-work.png')}
            alt={getHeaderText()}
          />
        );
      case Evaluation.GOOD_JOB:
        return (
          <img
            className='image-badge'
            src={prodPathReplace('images/good-job.png')}
            alt={getHeaderText()}
          />
        );
      case Evaluation.EXCELLENT:
        return (
          <img
            className='image-badge'
            src={prodPathReplace('images/excellent.png')}
            alt={getHeaderText()}
          />
        );
      default:
        return null;
    }
  };

  const styles = css`
    .feedback-header {
      background-color: ${getHeaderBackGroundColor()};
    }
    .feedback-body {
      .border-bottom {
        border-color: ${gray5} !important;
      }
    }
    img.image-badge {
      width: 300px;
      height: 60px;
    }
  `;

  return (
    <div css={styles} className='d-flex flex-column w-100'>
      <div className='feedback-header py-4 px-5 d-flex flex-column align-items-center text-align-center'>
        {renderBadge()}
        <p className='page-title mt-2'>{getHeaderText()}</p>
        <p className='text-regular mb-0'>{overallSummary}</p>
      </div>
      <div className='feedback-body px-5 py-2'>
        {whereYouDidWell?.length > 0 && (
          <div className='mb-4'>
            <div className='d-flex border-bottom py-2 align-items-center'>
              <Like width={36} height={36} className='mr-2' />
              <p className='text-regular font-weight-bold mb-0'>{t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.DID_WELL()}</p>
            </div>
            <ul className='pl-4 mb-0 pt-2'>
              {whereYouDidWell.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index} className='text-regular mb-1 ml-4'>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
        {whereYouCanGrow?.length > 0 && (
          <div>
            <div className='d-flex border-bottom py-2 align-items-center'>
              <InsightGrow width={36} height={36} className='mr-2' />
              <p className='text-regular font-weight-bold mb-0'>{t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.YOU_NEED_GROW()}</p>
            </div>
            <ul className='pl-4 mb-0 pt-2'>
              {whereYouCanGrow.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index} className='text-regular mb-1 ml-4'>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className='py-6 d-flex justify-content-center'>
          <NvGradientBorderButton
            onClick={handleStartConversation}
            className='align-items-center py-2 px-5 practice-again-button'
            ariaLabel={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.PRACTICE_AGAIN()}
            disabled={isRolePlayStarting || isHardDeadlineExpired}
          >
            {(isRolePlayStarting || isHardDeadlineExpired)
              ? <NvIcon icon='gen-ai' size='small' className='pr-2 text-gray-4' />
              : <GradientAiIcon icon='gen-ai' size='small' className='pr-2' />}
            <span className={`${(isRolePlayStarting || isHardDeadlineExpired) ? 'text-gray-4' : ''} text-regular bold`}>
              {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.PRACTICE_AGAIN()}
            </span>
          </NvGradientBorderButton>
        </div>
      </div>
    </div>
  );
};

export default RolePlayFeedback;
